import React, { useCallback, useMemo, useState } from 'react'
import ImageUploader from '../image-uploader'
import CompanySearch from '../company-search'
import { companyAdminPath } from '../impact-network-id'

import { Customer } from '@ecountabl/lib'

type FeaturedBusinesses = Customer['featuredBusinesses']

type Props = {
  value?: FeaturedBusinesses
  onChange: (value: FeaturedBusinesses) => void
}

type SectionKey = 'primary' | 'secondary'

type FeaturedSectionItem = {
  id: string
  name: string
  description?: string
}

type FeaturedSection = {
  title?: string
  bannerImage?: string
  featuredHeader?: string
  featuredSubtext?: string
  items?: FeaturedSectionItem[]
}

const EMPTY_SECTION: FeaturedSection = {
  title: '',
  bannerImage: '',
  featuredHeader: '',
  featuredSubtext: '',
  items: [],
}

const SECTION_CONFIG: Array<{
  key: SectionKey
  cardTitle: string
  emptyMessage: string
}> = [
  {
    key: 'primary',
    cardTitle: 'Primary',
    emptyMessage: 'No primary businesses configured',
  },
  {
    key: 'secondary',
    cardTitle: 'Secondary',
    emptyMessage: 'No secondary businesses configured',
  },
]

const FeaturedBusinessesList: React.FC<Props> = ({ value, onChange }: Props) => {
  const sectionsByKey = useMemo<Record<SectionKey, FeaturedSection>>(() => ({
    primary: value?.primary || EMPTY_SECTION,
    secondary: value?.secondary || EMPTY_SECTION,
  }), [value])

  // drag state
  const [draggingIndexBySection, setDraggingIndexBySection] = useState<Record<SectionKey, number | null>>({
    primary: null,
    secondary: null,
  })
  const [dragOverIndexBySection, setDragOverIndexBySection] = useState<Record<SectionKey, number | null>>({
    primary: null,
    secondary: null,
  })

  const reorder = useCallback(<T,>(list: T[], startIndex: number, endIndex: number): T[] => {
    const result = [...list]
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }, [])

  const getItems = (key: SectionKey): FeaturedSectionItem[] => {
    return sectionsByKey[key].items || []
  }

  const setSection = (key: SectionKey, nextSection: FeaturedSection): void => {
    onChange({
      ...(value || {}),
      [key]: nextSection,
    })
  }

  const updateSection = (key: SectionKey, field: keyof FeaturedSection, nextValue: string) => {
    const section = sectionsByKey[key]
    setSection(key, {
      ...section,
      [field]: nextValue,
    })
  }

  const selectItem = (key: SectionKey, id: string, name: string) => {
    const section = sectionsByKey[key]
    const items = [...(section.items || [])]
    if (items.some((item) => item.id === id)) {
      return
    }
    items.push({ id, name })
    setSection(key, { ...section, items })
  }

  const updateItem = (key: SectionKey, index: number, field: keyof FeaturedSectionItem, newValue: string) => {
    const section = sectionsByKey[key]
    const items = [...(section.items || [])]
    items[index] = { ...items[index], [field]: newValue }
    setSection(key, { ...section, items })
  }

  const removeItem = (key: SectionKey, index: number) => {
    const section = sectionsByKey[key]
    const items = [...(section.items || [])]
    items.splice(index, 1)
    setSection(key, { ...section, items })
  }

  const onDragStart = (key: SectionKey, index: number) => {
    setDraggingIndexBySection((prev) => ({ ...prev, [key]: index }))
  }

  const onDragEnter = (key: SectionKey, index: number) => {
    setDragOverIndexBySection((prev) => ({ ...prev, [key]: index }))
  }

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (ev) => {
    ev.preventDefault()
  }

  const clearDragState = (key: SectionKey) => {
    setDraggingIndexBySection((prev) => ({ ...prev, [key]: null }))
    setDragOverIndexBySection((prev) => ({ ...prev, [key]: null }))
  }

  const onDrop = (key: SectionKey) => {
    const draggingIndex = draggingIndexBySection[key]
    const dragOverIndex = dragOverIndexBySection[key]
    if (draggingIndex === null || dragOverIndex === null) {
      clearDragState(key)
      return
    }
    if (draggingIndex !== dragOverIndex) {
      const section = sectionsByKey[key]
      const items = reorder(getItems(key), draggingIndex, dragOverIndex)
      setSection(key, { ...section, items })
    }
    clearDragState(key)
  }

  return (
    <div className="data-form" style={{ width: '100%' }}>
      <h4>Featured Businesses</h4>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '1em', flexWrap: 'wrap' }}>
        {SECTION_CONFIG.map((config) => {
          const section = sectionsByKey[config.key]
          const items = getItems(config.key)
          const dragOverIndex = dragOverIndexBySection[config.key]
          return (
            <div key={config.key} className="card" style={{ marginBottom: '1.5em' }}>
              <div className="card-body">
                <h5 className="card-title">{config.cardTitle}</h5>
                <div className="form-group">
                  <label>Banner Image</label>
                  <div>
                    <ImageUploader
                      imgSrc={section.bannerImage || ''}
                      onChange={(imgUrl) => updateSection(config.key, 'bannerImage', imgUrl)}
                      targetSize={{ width: 500, height: 200 }}
                      style={{ width: '500px', height: '200px' }}
                      hidePrompt
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={section.title || ''}
                    onChange={(ev) => updateSection(config.key, 'title', ev.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Featured Header</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Default: "${section.title || ''} brands for you"`}
                    value={section.featuredHeader || ''}
                    onChange={(ev) => updateSection(config.key, 'featuredHeader', ev.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Featured Subtext</label>
                  <textarea
                    className="form-control"
                    value={section.featuredSubtext || ''}
                    onChange={(ev) => updateSection(config.key, 'featuredSubtext', ev.target.value)}
                    placeholder="supporting subtext"
                  />
                </div>
                <div className='separator' />
                <div className="form-group">
                  <label>Companies (in order) ({items.length})</label>
                </div>
                <div style={{ marginTop: '0.5em', width: '100%' }}>
                  {!items.length ? <div className="alert alert-secondary">{config.emptyMessage}</div> : null}
                  {items.map((item, i) => (
                    <div
                      key={i}
                      className="form-inline-row"
                      style={{ flexDirection: 'row', border: (dragOverIndex === i ? '1px dashed #666' : undefined) }}
                      draggable
                      onDragStart={() => onDragStart(config.key, i)}
                      onDragEnter={() => onDragEnter(config.key, i)}
                      onDragOver={onDragOver}
                      onDragEnd={() => clearDragState(config.key)}
                      onDrop={() => onDrop(config.key)}
                    >
                      <div
                        aria-label="Drag to reorder"
                        title="Drag to reorder"
                        style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#888' }}
                      >
                        <span style={{ fontSize: '18px', lineHeight: '18px', cursor: 'grab' }}>⋮⋮</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        {item?.id ? (
                          <>
                            <div style={{ paddingTop: '0.5em' }}>
                              <strong style={{ cursor: 'grab' }}>{item.name}</strong>
                            </div>
                            <div style={{ color: '#888', fontSize: '0.9em' }}>
                              {item.id}
                              <a
                                style={{ marginLeft: '0.5em', cursor: 'pointer' }}
                                onClick={() => window.open(companyAdminPath(item.id), 'new')}
                                title="Open company"
                              >
                                <i className="fa fa-external-link" />
                              </a>
                            </div>
                          </>
                        ) : null}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '1em', flex: 1 }}>
                        <label>Description</label>
                        <textarea
                          className="form-control"
                          value={item.description || ''}
                          onChange={(ev) => updateItem(config.key, i, 'description', ev.target.value)}
                          placeholder="short description"
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginLeft: '0.5em' }}>
                        <i
                          className="fa fa-trash"
                          style={{ cursor: 'pointer', fontSize: '1.5em', margin: '0.5em 0' }}
                          onClick={() => removeItem(config.key, i)}
                        />
                      </div>
                    </div>
                  ))}
                  <div className='separator' />
                  <div className="">
                    <label>Add a Business</label>
                    <CompanySearch
                      resultsStyle="select"
                      clearAfterSelect
                      onSelect={(selectedId, co) => {
                        selectItem(config.key, selectedId, co?.name || '')
                      }}
                      placeholder="Search companies..."
                      autoFocus={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FeaturedBusinessesList